'use client';

import React from 'react';

export default function RGPSHero() {
  const scrollTo = (id: string) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="home"
      className="relative flex items-center justify-center overflow-hidden"
      style={{ height: '90vh', minHeight: '560px' }}
    >
      {/* Background Image — desktop: full cinematic, mobile: natural crop keeping house & driveway */}
      <div
        className="absolute inset-0 bg-no-repeat"
        style={{
          backgroundImage: `url('/assets/images/nm_hero_bg.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center 40%',
        }}
      />

      {/* Mobile-specific crop: shift focus slightly lower to keep driveway visible */}
      <style>{`
        @media (max-width: 640px) {
          #home .hero-bg {
            background-position: 55% 55% !important;
          }
        }
      `}</style>

      {/* Multi-layer dark gradient overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black/75" />
      {/* Extra left-side vignette for cinematic feel */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/20" />
      {/* Subtle teal accent at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-black/60 to-transparent" />

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center pt-20 pb-12">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-teal/20 backdrop-blur-sm border border-teal/40 text-teal-light px-4 py-2 rounded-full text-sm font-semibold mb-6">
          <span className="w-2 h-2 rounded-full bg-teal animate-pulse" />
          Serving All of New Mexico
        </div>

        {/* Headline */}
        <h1
          className="text-white font-bold leading-tight mb-6"
          style={{
            fontSize: 'clamp(1.85rem, 4.5vw, 3.75rem)',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            textShadow: '0 2px 20px rgba(0,0,0,0.6)',
          }}
        >
          Professional Exterior Cleaning That Makes Your{' '}
          <span className="text-teal">Property Look Brand New</span>
        </h1>

        {/* Subheadline */}
        <p
          className="text-white/85 text-base sm:text-lg max-w-3xl mx-auto mb-8 leading-relaxed"
          style={{ textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}
        >
          Pressure washing, house washing, roof cleaning, window cleaning, solar panel cleaning,
          gutter cleaning, trash bin cleaning, and commercial exterior cleaning throughout New Mexico.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
          <button
            onClick={() => scrollTo('#estimate')}
            className="w-full sm:w-auto bg-teal hover:bg-teal-dark text-white font-bold text-lg px-8 py-4 rounded-full shadow-teal transition-all hover:scale-105 hover:shadow-teal-lg"
          >
            Get My Free Estimate
          </button>
          <button
            onClick={() => scrollTo('#services')}
            className="w-full sm:w-auto bg-white/15 hover:bg-white/25 backdrop-blur-sm border-2 border-white/60 text-white font-bold text-lg px-8 py-4 rounded-full transition-all hover:scale-105"
          >
            View Our Services
          </button>
        </div>

        {/* Trust Badges */}
        <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-5">
          {[
            '✓ Free Estimates',
            '✓ Fully Insured',
            '✓ Residential & Commercial',
            '✓ Satisfaction Guaranteed',
          ].map((badge) => (
            <div
              key={badge}
              className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/25 text-white text-sm font-semibold px-4 py-2 rounded-full"
              style={{ textShadow: '0 1px 4px rgba(0,0,0,0.4)' }}
            >
              {badge}
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/60">
        <span className="text-xs font-medium tracking-wide uppercase">Scroll to explore</span>
        <div className="w-5 h-8 border-2 border-white/30 rounded-full flex items-start justify-center p-1">
          <div className="w-1 h-2 bg-white/60 rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
}
